import { useReadQuery, useSuspenseQuery } from '@apollo/client/react';
import { useTransition, useRef, useCallback, useMemo, startTransition, useState } from 'react';
import { debounce } from 'lodash';
import { Plus, UserRoundSearch } from 'lucide-react';
import { toast } from 'sonner';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { QueryOrderEnum, UserColumnsEnum } from 'src/graphql/schema';
import { withSuspense } from 'src/hoc';
import { InputSearch } from '@/components/ui/input-search';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Card } from '@/components/ui/card';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';

import { useAppMembershipsManagement, type IPendingMember } from '../model';

import type { IUsersQuery } from 'src/graphql/actions/user/users.query';
import type { IAppMembershipsQuery } from 'src/graphql/actions/appMemberships/appMemberships.query';

import AddUserDialog from './AddUserDialog';

type IUserRow = {
  id: string;
  user: {
    name: string;
    email: string;
  };
};

const AddMemberButton = ({ id = '', name = '', email = '' }) => {
  const { addPendingMember } = useAppMembershipsManagement();

  const onAdd = () => addPendingMember({ id, name, email });

  return (
    <Button variant="ghost" size="sm" className="shrink-0" onClick={onAdd}>
      Add
      <Plus />
    </Button>
  );
};

const columnHelper = createColumnHelper<IUserRow>();
const userColumn = columnHelper.accessor('user', {
  cell: (props) => {
    const { name, email } = props.getValue();
    const fallback = name?.charAt(0).toUpperCase();

    return (
      <div className="flex items-center gap-2">
        <Avatar className="size-7 rounded-full shrink-0">
          <AvatarImage src={getGravatarUrl(email)} />
          <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
        </Avatar>
        <div className="grow min-w-0">
          <div className="text-sm text-foreground font-semibold truncate">{name}</div>
          <div className="text-xs text-muted-foreground truncate">{email}</div>
        </div>
      </div>
    );
  },
});
const addMemberColumn = columnHelper.accessor('id', {
  cell: (props) => (
    <div className="flex justify-end">
      <AddMemberButton
        id={props.row.original.id}
        name={props.row.original.user.name}
        email={props.row.original.user.email}
      />
    </div>
  ),
});

const USERS_PER_PAGE = 20;

const OrganizationMembers = () => {
  const [queryFilter, setQueryFilter] = useState<string | undefined>(undefined);
  const [isPending, startSearchTransition] = useTransition();
  const tableRef = useRef<HTMLDivElement>(null);
  const requestedFromRef = useRef<IUsersQuery['users']['nodes'] | null>(null);
  const { queryAppMembershipsRef, canCreateMember, pendingMembers } = useAppMembershipsManagement();
  const {
    data: { users },
    refetch,
    fetchMore,
  } = useSuspenseQuery(UsersDocument, {
    variables: {
      page: 1,
      perPage: USERS_PER_PAGE,
      orderBy: UserColumnsEnum.CreatedAt,
      order: QueryOrderEnum.Desc,
      ...(queryFilter && { queryFilter }),
    },
  });
  const {
    data: { appMemberships },
  } = useReadQuery(queryAppMembershipsRef);

  const nextPage = Math.ceil(users.nodes.length / USERS_PER_PAGE) + 1;

  const handleLoadMore = () => {
    if (requestedFromRef.current === users.nodes) return Promise.resolve();

    requestedFromRef.current = users.nodes;

    return new Promise<void>((resolve) => {
      startTransition(async () => {
        try {
          await fetchMore({
            variables: {
              page: nextPage,
              perPage: USERS_PER_PAGE,
              orderBy: UserColumnsEnum.CreatedAt,
              order: QueryOrderEnum.Desc,
              ...(queryFilter && { queryFilter }),
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult?.users?.nodes?.length) return prev;

              const loadedIds = new Set(prev.users.nodes.map(({ id }) => id));

              return {
                ...prev,
                users: {
                  ...fetchMoreResult.users,
                  nodes: [...prev.users.nodes, ...fetchMoreResult.users.nodes.filter(({ id }) => !loadedIds.has(id))],
                },
              };
            },
          });
        } catch (error) {
          requestedFromRef.current = null;
          toast.error('Failed to load more users');
        } finally {
          resolve();
        }
      });
    });
  };

  const { sentinelRef } = useInfiniteScroll({
    rootRef: tableRef,
    hasNextPage: users.hasNextPage && !isPending,
    onLoadMore: handleLoadMore,
  });

  const debouncedOnSearch = useRef(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value;
      startSearchTransition(() => setQueryFilter(searchValue));
    }, 500),
  ).current;

  const handleClear = () => {
    debouncedOnSearch.cancel();
    startSearchTransition(() => setQueryFilter(undefined));
  };

  const onClear = useCallback(handleClear, [refetch]);

  const filteredUsers = useMemo(
    () => filterUsers(users.nodes, appMemberships.nodes, pendingMembers),
    [users.nodes, appMemberships.nodes, pendingMembers],
  );

  const columns = useMemo(() => (canCreateMember ? [userColumn, addMemberColumn] : [userColumn]), [canCreateMember]);

  const table = useReactTable({
    data: filteredUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-2.5 overflow-hidden flex flex-col py-3.5 px-4">
      <h2 className="text-sm text-foreground">Organization members</h2>
      <InputSearch
        placeholder="Search name or email"
        onChange={debouncedOnSearch}
        onClear={onClear}
        isLoading={isPending}
      />
      <Card className="rounded-md">
        <Table ref={tableRef} maxHeight="max-h-[280px]" className="table-fixed">
          <colgroup>
            <col />
            {canCreateMember && <col className="w-25" />}
          </colgroup>
          <TableBody>
            {filteredUsers.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-white">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-1.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-6">
                  <EmptyBlock
                    withCard={false}
                    icon={UserRoundSearch}
                    title={queryFilter ? 'No results found' : 'You don’t have members in your organization'}
                    action={<AddUserDialog onSuccess={handleClear} />}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}

            <TableRow className="border-0 hover:bg-white">
              <TableCell colSpan={columns.length} className="p-0">
                <div ref={sentinelRef} aria-hidden="true" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default withSuspense(OrganizationMembers);

function filterUsers(
  users: IUsersQuery['users']['nodes'],
  memberships: IAppMembershipsQuery['appMemberships']['nodes'],
  pendingMembers: IPendingMember[],
) {
  const memberIds = new Set(memberships.map(({ user: { id } }) => id));
  const pendingIds = new Set(pendingMembers.map(({ id }) => id));

  return users
    .filter(({ id }) => !memberIds.has(id) && !pendingIds.has(id))
    .map((user) => ({
      id: user.id,
      user: {
        name: user.name || '',
        email: user.email,
      },
    }));
}
